<script setup lang="ts">
import type {
  GraphqlDatatableColumn,
  GraphqlDatatableFilter,
} from '~/components/GqlDatatable.vue';
import { DownOutlined, SettingOutlined } from '@ant-design/icons-vue';
import { computed, ref } from 'vue';
import tipoPerfil from '~/constants/tipoPerfil';
import publicacaoNumeroProcessoDatatableGqlQuery from '~/graphql/publicacaoNumeroProcessoDatatable.graphql';
import { useAuthStore } from '~/stores/auth';
import { useDrawerStore } from '~/stores/drawer';
import { formatCnj, onlyNumbers } from '~/utils';

interface Props {
  disabledFilters?: string[];
  variables?: any;
  keepHistory?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabledFilters: () => [],
  variables: () => {
    return {
      order: [
        {
          publicacao: {
            dataMovimento: 'DESC',
          },
          codigo: 'ASC',
        },
      ],
    };
  },
});

const authStore = useAuthStore();
const drawerStore = useDrawerStore();
const selectedRowKeys = ref<string[]>([]);
const gqlVariables = ref<any>(props.variables ? { ...props.variables } : {});
const tableRef = ref<any>(null);

const filters = [] as GraphqlDatatableFilter[];

if (!props.disabledFilters.find((x) => x === 'codigo')) {
  filters.push({
    key: 'codigo',
    label: 'Código',
    path: 'publicacao.codigo',
    type: 'text-multiple',
    queryKey: 'c',
    inWhereClause: true,
    componentProps: {
      placeholder: 'Digite um ou mais códigos',
    },
  });
}

if (!props.disabledFilters.find((x) => x === 'numeroPRocesso')) {
  filters.push({
    key: 'numeroProcesso',
    label: 'Número de processo',
    path: 'numeroProcesso',
    type: 'text-multiple',
    queryKey: 'n',
    inWhereClause: true,
    componentProps: {
      placeholder: 'Digite um ou mais números de processo',
    },
    parseValue: (value: any) => {
      return value.split(',').map((x: any) => onlyNumbers(x.trim()));
    },
  });
}

if (!props.disabledFilters.find((x) => x === 'dataMovimento')) {
  filters.push({
    componentProps: {
      format: 'DD/MM/YYYY',
      placeholder: ['Inicial', 'Final'],
      size: 'large',
      style: 'width: 100%',
      valueFormat: 'DD/MM/YYYY',
    },
    key: 'dataMovimento',
    label: 'Data de movimentação',
    path: 'publicacao.dataMovimento',
    inWhereClause: true,
    queryKey: 'd',
    type: 'date',
  });
}

const colunas = [
  {
    dataIndex: 'numeroProcesso',
    key: 'numeroProcesso',
    title: 'NÚMERO DO PROCESSO',
    width: 250,
  },
  {
    dataIndex: ['publicacao', 'dataMovimentoText'],
    key: 'dataMovimento',
    sortBy: ['publicacao', 'dataMovimento'],
    sorter: true,
    defaultSortOrder: 'descend',
    alwaysOrderBy: {
      codigo: 'ASC',
    },
    title: 'DATA DISPONIBILIZAÇÃO',
    width: 220,
  },
  {
    dataIndex: ['publicacao', 'diario'],
    key: 'diario',
    title: 'DIÁRIO',
    width: 250,
  },
  {
    dataIndex: ['publicacao', 'caderno'],
    key: 'caderno',
    title: 'CADERNO',
    width: 250,
  },
  {
    dataIndex: ['publicacao', 'pagina'],
    key: 'pagina',
    title: 'PÁGINA',
    width: 50,
  },
  {
    key: 'conteudo',
    title: 'CONTEÚDO',
  },
] as GraphqlDatatableColumn[];

function onSelectChange(keys: string[]) {
  selectedRowKeys.value = [...keys];
}

function abrirForm({
  title,
  formName,
  publicacaoNumeroProcessoCodigos,
}: {
  title: string;
  formName: string;
  publicacaoNumeroProcessoCodigos: string[];
}) {
  drawerStore.push2({
    componentName: 'CompromissoForm3',
    params: {
      componentName: formName,
      componentProps: {
        publicacaoNumeroProcessoCodigos,
      },
      title,
    },
    onClose: async () => {
      await tableRef.value?.reloadAsync();
    },
  });
}

async function acoesSelecionadosOnClick({ key }: { key: any }) {
  if (key === 'solicitar-verificacao') {
    drawerStore.push2({
      componentName: 'CompromissoForm2',
      onClose: async () => {
        await tableRef.value?.reloadAsync();
      },
      params: {
        componentName: 'SolicitarVerificacaoPublicacaoCapturadaForm',
        componentProps: {
          publicacaoNumeroProcessoCodigos: [...selectedRowKeys.value],
        },
        title: 'SOLICITAR VERIFICAÇÃO DA PUBLICAÇÃO',
      },
      width: 600,
    });
  }
  if (key === 'criar-compromisso-pub') {
    abrirForm({
      formName: 'CriarCompromissoPubForm',
      title: 'CRIAR COMPROMISSO PUB',
      publicacaoNumeroProcessoCodigos: [...selectedRowKeys.value],
    });
  }

  if (key === 'criar-compromisso-caq') {
    drawerStore.push2({
      componentName: 'CompromissoForm3',
      onClose: async () => {
        await tableRef.value?.reloadAsync();
      },
      params: {
        componentName: 'CriarCompromissoCaqForm',
        componentProps: {
          publicacaoNumeroProcessoCodigos: [...selectedRowKeys.value],
        },
        title: 'CRIAR COMPROMISSO CAQ',
      },
      width: 600,
    });
  }
}

function abrirPublicacaoTexto(publicacao: any) {
  drawerStore.push({
    componentName: 'PublicacaoTextoView',
    params: {
      texto: publicacao.conteudo,
    },
  });
}

function abrirFluxo(publicacaoNumeroProcessoCodigo: string) {
  drawerStore.push2({
    componentName: 'FluxosView',
    params: {
      codigoPublicacaoNumeroProcesso: publicacaoNumeroProcessoCodigo,
    },
  });
}

const mostrarAcoes = computed(() => {
  return (
    selectedRowKeys.value.length > 0 &&
    (authStore.temPerfilAcesso(
      tipoPerfil.AutomatizacaoSolicitarVerificacaoPublicacaoEmLote,
    ) ||
      authStore.temPerfilAcesso(
        tipoPerfil.AutomatizacaoCriarCompromissoPubAPartirPublicacaoEmLote,
      ) ||
      authStore.temPerfilAcesso(
        tipoPerfil.CriarCompromissoCaqAPartirPublicacao,
      ))
  );
});
</script>

<template>
  <GqlDatatable
    ref="tableRef"
    :query="publicacaoNumeroProcessoDatatableGqlQuery"
    :columns="colunas"
    :variables="gqlVariables"
    :keep-history="props.keepHistory"
    :filters="filters"
    row-key="codigo"
    :row-selection="{
      selectedRowKeys,
      onChange: onSelectChange,
    }"
  >
    <template #topActions>
      <a-dropdown v-if="mostrarAcoes">
        <template #overlay>
          <a-menu @click="acoesSelecionadosOnClick">
            <a-menu-item
              v-if="
                authStore.temPerfilAcesso(
                  tipoPerfil.AutomatizacaoSolicitarVerificacaoPublicacaoEmLote,
                )
              "
              key="solicitar-verificacao"
            >
              Solicitar verificação
            </a-menu-item>
            <a-menu-item
              v-if="
                authStore.temPerfilAcesso(
                  tipoPerfil.AutomatizacaoCriarCompromissoPubAPartirPublicacaoEmLote,
                )
              "
              key="criar-compromisso-pub"
            >
              Criar compromisso pub
            </a-menu-item>
            <a-menu-item
              v-if="
                authStore.temPerfilAcesso(
                  tipoPerfil.CriarCompromissoCaqAPartirPublicacao,
                )
              "
              key="criar-compromisso-caq"
            >
              Criar compromisso CAQ
            </a-menu-item>
          </a-menu>
        </template>
        <a-button size="large">
          <template #icon>
            <SettingOutlined />
          </template>
          Ações
          <DownOutlined />
        </a-button>
      </a-dropdown>
    </template>
    <template #numeroProcessoColumn="{ record }">
      <a
        href="#"
        style="text-transform: uppercase"
        @click.prevent="() => abrirFluxo(record.codigo)"
        >{{ formatCnj(record.numeroProcesso) }}</a
      >
    </template>
    <template #conteudoColumn="{ record }">
      <a-typography-paragraph
        :ellipsis="{ rows: 2, expandable: true }"
        :content="record.publicacao?.conteudo"
        style="
          margin-bottom: 0;
          white-space: pre-wrap;
          color: #389393;
          cursor: pointer;
        "
        @click="
          () => {
            abrirPublicacaoTexto(record.publicacao);
          }
        "
      />
    </template>
  </GqlDatatable>
</template>
